import { useState } from 'react';

import { AddProfileImage } from '@components/add-profile-image';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconEdit } from '@components/icons/edit';
import { useSidebar } from '@components/sidebar';
import { hideSidebar, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';

import { useEditUser, useRoleOptions, useUsers } from 'api/users/hooks';
import { User, UserRole } from 'api/users/requests';

import { useRoleChangeModal } from './role-change-modal';

export const useEditTeamUserSidebar = () => {
  const sidebar = useSidebar(EditTeamUser, {
    variant: 'REGULAR',
  });
  return sidebar;
};

type UserFormData = {
  name: string;
  email: string;
  phone: string;
  role: UserRole;
};

const Form = createForm<UserFormData>();

export const EditTeamUser = ({ user }: { user: User }) => {
  const { t } = useScopedTranslate(locale);

  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);
  const [emailError, setEmailError] = useState([] as string[]);

  const { data: users } = useUsers();
  const showRoleChangeModal = useRoleChangeModal();
  const roleOptions = useRoleOptions();

  const adminCount =
    users?.filter((u) => {
      return u.role === 'admin';
    }).length ?? 0;
  const isLastAdmin = user.role === 'admin' && adminCount <= 1;
  const isSelf = user.isMe;

  const editMutation = useEditUser(user?.id ?? '', setEmailError, {
    onSuccess: hideSidebar,
  });

  const editMutationForSelfDemotion = useEditUser(user?.id ?? '', setEmailError, {
    skipSuccessToast: true,
  });

  const formStore = useCreateFormStore();
  const name = formStore.useFieldValue('name') as string;

  const handleSave = (data: UserFormData) => {
    const roleChanged = data.role !== user.role;
    const isSelfDemotion = isSelf && roleChanged && data.role !== 'admin';

    if (isSelfDemotion) {
      showRoleChangeModal({
        newRole: data.role,
        onConfirm: () => {
          return submitFormForSelfDemotion(data);
        },
      });
      return;
    }

    return submitForm(data);
  };

  const buildFormData = (data: UserFormData) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);
    formData.append('role', data.role);

    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }
    return formData;
  };

  const submitForm = (data: UserFormData) => {
    return editMutation.mutate(buildFormData(data));
  };

  const submitFormForSelfDemotion = async (data: UserFormData) => {
    await editMutationForSelfDemotion.mutateAsync(buildFormData(data));
  };

  const roleSelect = (
    <Form.Select
      id="role"
      label={t('role')}
      options={roleOptions}
      defaultValue={user?.role ?? 'editor'}
      isDisabled={isLastAdmin}
    />
  );

  return (
    <Sidebar>
      <Sidebar.SimpleHeader icon={IconEdit} color="elementBlue100" title={t('editMember')} />
      <Sidebar.Content>
        <Form store={formStore} onSubmit={handleSave}>
          <Stack space={4} orientation="horizontal" align="center">
            <AddProfileImage
              name={name}
              initalPicture={user?.picture}
              picture={picture}
              setPicture={setPicture}
              clearPicture={clearPicture}
              setClearPicture={setClearPicture}
            />
            <Text font="semibold14" text={t('profileImage')} />
          </Stack>
          <Form.TextField
            id="name"
            label={t('core.fullName')}
            validators={['required']}
            defaultValue={user?.name ?? ''}
          />
          <Form.TextField
            id="email"
            label={t('core.email')}
            validators={['required', 'email']}
            defaultValue={user?.email}
          />
          {emailError && <FormError text={(emailError ?? []).join(' ')} />}
          <Form.PhoneField
            id="phone"
            label={t('core.phone')}
            validators={[]}
            defaultValue={user?.phone ?? ''}
          />
          {isLastAdmin ? (
            <Tooltip content={t('lastAdminTooltip')}>
              <div>{roleSelect}</div>
            </Tooltip>
          ) : (
            roleSelect
          )}
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit text={t('updateMember')} variant="primary-blue" />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const locale = {
  en: {
    updateMember: 'Update member',
    editMember: 'Edit member',
    profileImage: 'Profile image',
    role: 'Role',
    lastAdminTooltip: 'Cannot change role of the last admin',
  },
};
